import { arrayUnion, serverTimestamp, Timestamp } from 'firebase/firestore';
import type { Ticket, TicketPriority, TicketRepeat, TicketStatus } from '@elka/types';

/** What a caller supplies for a new ticket. Empty optionals are left out of the document. */
export interface NewTicketInput {
  title: string;
  description?: string;
  epicId?: string;
  assigneeId?: string;
  assigneeName?: string;
  priority?: TicketPriority; // default 'normal'
  dueDate?: string; // YYYY-MM-DD
  customer?: string;
  site?: string;
  repeat?: TicketRepeat;
  dealId?: string;
  dealName?: string;
}

/**
 * The document the tasks app writes for a new ticket. Customer and site are stored only on
 * a standalone task (a task in a project shows the project's).
 */
export function newTicketDoc(
  input: NewTicketInput,
  actor: { uid: string; name: string },
  status: TicketStatus = 'backlog'
) {
  return {
    title: input.title,
    ...(input.epicId ? { epicId: input.epicId } : {}),
    ...(input.description ? { description: input.description } : {}),
    ...(input.assigneeId ? { assigneeId: input.assigneeId, assigneeName: input.assigneeName } : {}),
    priority: input.priority ?? 'normal',
    ...(input.dueDate ? { dueDate: input.dueDate } : {}),
    ...(!input.epicId && input.customer ? { customer: input.customer } : {}),
    ...(!input.epicId && input.site ? { site: input.site } : {}),
    ...(input.repeat ? { repeat: input.repeat } : {}),
    ...(input.dealId ? { dealId: input.dealId } : {}),
    ...(input.dealName ? { dealName: input.dealName } : {}),
    status,
    // Client Timestamp.now(), because serverTimestamp() can't go inside an array.
    statusHistory: [{ status, at: Timestamp.now(), by: actor.name }],
    subtasks: [],
    userId: actor.uid,
    createdBy: actor.name,
    createdAt: serverTimestamp(),
  };
}

/**
 * Patch for a plain status move. Recurring tickets must be completed through the tasks
 * app: the next occurrence is created there, in the same batch as the move.
 */
export function ticketMovePatch(to: TicketStatus, by: string) {
  return {
    status: to,
    statusHistory: arrayUnion({ status: to, at: Timestamp.now(), by }),
    updatedAt: serverTimestamp(),
    updatedBy: by,
  };
}

/** Patch for a due-date change, with the changeLog entry the tasks app writes for one. */
export function ticketDueDatePatch(ticket: Pick<Ticket, 'dueDate'>, dueDate: string, by: string) {
  return {
    dueDate,
    changeLog: arrayUnion({
      at: Timestamp.now(),
      by,
      field: 'dueDate',
      ...(ticket.dueDate ? { from: ticket.dueDate } : {}),
      to: dueDate,
    }),
    updatedAt: serverTimestamp(),
    updatedBy: by,
  };
}
